import { Injectable, signal } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Theme } from '@core/enums/theme.enum';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  readonly currentTheme = signal<Theme>(Theme.System);

  async init() {
    const { value } = await Preferences.get({ key: 'theme' });

    await this.setTheme((value as Theme) || Theme.System);
  }

  async setTheme(theme: Theme) {
    this.currentTheme.set(theme);

    await Preferences.set({
      key: 'theme',
      value: theme,
    });

    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)',
    ).matches;

    const dark =
      theme === Theme.Dark || (theme === Theme.System && prefersDark);

    document.documentElement.classList.toggle('ion-palette-dark', dark);
    document.documentElement.classList.toggle('dark', dark);

    await this.applyNativeStatusBar(dark);
  }

  toggle() {
    const next = this.currentTheme() === Theme.Dark ? Theme.Light : Theme.Dark;
    this.setTheme(next);
  }

  /**
   * Keep the system status bar overlaid (CSS owns the inset) and flip icon
   * colour with the palette. StatusBar.Style.Dark is light glyphs for a dark
   * background — Capacitor's naming, not the app theme.
   */
  private async applyNativeStatusBar(dark: boolean): Promise<void> {
    await Promise.allSettled([
      StatusBar.setOverlaysWebView({ overlay: true }),
      StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }),
    ]);
  }
}
